import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import IntroSplash from "@/components/site/IntroSplash";
import { PulseLogo } from "@/components/site/Header";
import { BACKEND } from "@/lib/releases";

const ReleaseCard = ({ item, i, big }) => (
  <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className={big ? "md:row-span-2" : ""}>
    <Link to={item.path} className="group block h-full" data-testid={`music-card-${item.slug}`}>
      <div className="relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]">
        <img src={`${BACKEND}${item.cover_url}`} alt={`${item.title} cover`}
             className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/15 font-mono text-[9px] tracking-[0.25em]">
          {item.kind === "album" ? "ALBUM" : "SINGLE"}
        </div>
        <div className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-300">
          <ArrowUpRight size={18} />
        </div>
      </div>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className={`font-display font-bold tracking-tight truncate ${big ? "text-3xl" : "text-xl"}`}>{item.title}</div>
          <div className="text-sm text-white/55 truncate">{item.artist} · {item.subtitle}</div>
        </div>
        <div className="font-mono text-xs text-white/60 shrink-0 pt-1.5" data-testid={`music-card-price-${item.slug}`}>${item.price.toFixed(2)}</div>
      </div>
    </Link>
  </motion.div>
);

export default function MusicCatalog() {
  const [items, setItems] = useState([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => { axios.get(`${BACKEND}/api/releases`).then((r) => setItems(r.data)).catch(() => setFailed(true)); }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="music-catalog-page">
      <Helmet><title>Music | Pulse Media</title></Helmet>
      <IntroSplash force />
      <header className="flex items-center justify-between px-6 lg:px-12 py-6">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="music-home-link">
          <PulseLogo size={36} />
          <span className="font-mono text-[10px] tracking-[0.35em] text-white/60">PULSE MEDIA</span>
        </Link>
        <Link to="/artists" className="text-xs font-bold tracking-[0.2em] text-white/60 hover:text-white transition-colors" data-testid="music-artists-link">ARTISTS</Link>
      </header>
      <main className="px-6 lg:px-12 pb-28 max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="pt-10 pb-14 md:pt-16 md:pb-20">
          <div className="font-mono text-[10px] tracking-[0.35em] text-white/45 mb-5">/ PULSE MEDIA RECORDS · CATALOG</div>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-9xl font-black tracking-tighter leading-[0.85]">MUSIC</h1>
          <p className="mt-6 text-white/55 max-w-md text-sm md:text-base">Preview, buy and download releases straight from the label.</p>
        </motion.div>
        {failed && <div className="text-white/55 text-sm py-10" data-testid="music-load-error">Couldn't load the catalog. Please refresh in a moment.</div>}
        <div className="grid md:grid-cols-[1.5fr_1fr] gap-8 md:gap-10" data-testid="music-catalog-grid">
          {items.map((it, i) => <ReleaseCard key={it.slug} item={it} i={i} big={i === 0} />)}
        </div>
      </main>
    </div>
  );
}
